import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserListHead from '../../components/_dashboard/user/UserListHead';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import SimplePagination from '../../components/SimplePagination';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import CallCenterService from '../services/CallCenterService';
import prospectService from '../services/prospectService';
import ProspectToolbar from './ProspectToolbar';
import useProspectSelector from '../../store/Slice/Prospect/ProspectSelector';
import { getProspectGridData } from '../../store/Slice/Prospect/ProspectSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDebounce from '../../utils/useDebounce';

const TABLE_HEAD = [
  { id: 'id', label: 'Prospect ID', alignRight: false },
  { id: 'created', label: 'Prospect Date', alignRight: false },
  { id: 'randomPhoneNumber', label: 'Mobile', alignRight: false },
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'loanType', label: 'Loan Type', alignRight: false },
  { id: 'source', label: 'Source', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'modifiedOn', label: 'Last Update On', alignRight: false },
  { id: 'createdBy', label: 'Assign to', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: { fontSize: 13, fontWeight: 'bold', padding: '6px' },
  [`&.${tableCellClasses.body}`]: { fontSize: 11, padding: '4px 6px' }
}));

const { ProspectDelete } = prospectService;
const { UpdateStatus } = CallCenterService;

export default function ProspectListView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ProspectGrid } = useProspectSelector();
  const { LoginData } = useLoginSelector();

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    name: '',
    status: ' ',
    source: ' ',
    city: null,
    dates: [{ startDate: null, endDate: null, key: 'selection' }]
  });
  const [ui, setUi] = useState({
    openAssign: false,
    assignValue: ' ',
    alertMsg: false,
    openDelete: false,
    leadId: null,
    page: 0,
    hasNextPage: true,
    refresh: Date.now()
  });

  const debouncedSearch = useDebounce(filters.name, 600);

  const handleRequestSort = useCallback(
    (e, property) => {
      if (property === 'action') return;
      setOrder((prev) => (orderBy === property && prev === 'asc' ? 'desc' : 'asc'));
      setOrderBy(property);
    },
    [orderBy]
  );

  const handleSelectAllClick = useCallback(
    (e) => {
      setSelected(
        e.target.checked ? (ProspectGrid?.prospects || []).filter((item) => !item.isDisabled) : []
      );
    },
    [ProspectGrid]
  );

  const handleAssignSubmit = useCallback(async () => {
    if (ui.assignValue === ' ') {
      setUi((prev) => ({ ...prev, alertMsg: true }));
      return;
    }
    const payload = selected.map((item) => ({
      conversionDetailsModel: { assignedTo: ui.assignValue },
      leadId: item.leadId,
      stageId: item.stageId,
      stageStatusCode: 'ASSIGNED',
      userId: LoginData?.userId
    }));
    const res = await UpdateStatus(payload, LoginData?.jwt);
    if (res?.msg === 'Forbidden') navigate('/');
    if (res?.status === 'success') {
      setUi((prev) => ({ ...prev, assignValue: ' ', openAssign: false, refresh: Date.now() }));
      setSelected([]);
    }
  }, [ui.assignValue, selected, LoginData, navigate]);

  const handleDelete = useCallback(async () => {
    const res = await ProspectDelete(
      { companyId: LoginData?.companyId, userId: LoginData?.userId, leadId: ui.leadId },
      LoginData?.jwt
    );
    if (res?.msg === 'Forbidden') navigate('/');
    if (res?.status === 'success')
      setUi((prev) => ({ ...prev, openDelete: false, refresh: Date.now() }));
  }, [ui.leadId, LoginData, navigate]);

  const credentials = useMemo(
    () => ({
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      leadsFilterModel: {
        fromLeadDate: filters.dates[0].startDate ? fDateTime2(filters.dates[0].startDate) : null,
        toLeadDate: filters.dates[0].endDate ? fDateTime2(filters.dates[0].endDate) : null,
        leadStageStatusId: filters.status !== ' ' ? filters.status : null,
        sourceTypeId: filters.source !== ' ' ? filters.source : null,
        cityId: filters.city ? filters.city.cityId : null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: 10,
        pageNumber: ui.page + 1,
        sortOrder: order
      }
    }),
    [
      LoginData,
      filters.dates,
      filters.status,
      filters.source,
      filters.city,
      debouncedSearch,
      ui.page,
      order
    ]
  );

  useEffect(() => {
    dispatch(getProspectGridData(credentials));
  }, [dispatch, credentials, ui.refresh]);
  useEffect(() => {
    setUi((prev) => ({ ...prev, hasNextPage: (ProspectGrid?.prospects || []).length >= 10 }));
  }, [ProspectGrid]);

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="List of Prospect" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <ProspectToolbar
              selected={selected}
              filterName={filters.name}
              setFilterName={(val) => setFilters((p) => ({ ...p, name: val }))}
              onFilterName={(e) => setFilters((p) => ({ ...p, name: e.target.value }))}
              parent="prospect"
              pageMenu={[]}
              selectStatus={filters.status}
              setSelectStatus={(val) => setFilters((p) => ({ ...p, status: val }))}
              onFilterStatus={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              selectSource={filters.source}
              setSelectSource={(val) => setFilters((p) => ({ ...p, source: val }))}
              onFilterSource={(e) => setFilters((p) => ({ ...p, source: e.target.value }))}
              cityListData={filters.city}
              setCityListData={(val) => setFilters((p) => ({ ...p, city: val }))}
              filterDatesRange={filters.dates}
              setFilterDatesRange={(val) => setFilters((p) => ({ ...p, dates: val }))}
              onAssignSubmit={handleAssignSubmit}
              openAssign={ui.openAssign}
              setOpenAssign={(val) => setUi((p) => ({ ...p, openAssign: val }))}
              assignValue={ui.assignValue}
              setAssignValue={(val) => setUi((p) => ({ ...p, assignValue: val }))}
              alertMsg={ui.alertMsg}
              setAlertMsg={(val) => setUi((p) => ({ ...p, alertMsg: val }))}
            />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={(ProspectGrid?.prospects || []).length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    disableBoxTrue={(ProspectGrid?.prospects || []).filter((i) => i.isDisabled)}
                    disableBoxFalse={(ProspectGrid?.prospects || []).filter((i) => !i.isDisabled)}
                    isCheckBox="true"
                  />
                  <TableBody>
                    {ProspectGrid?.prospects?.map((item, index) => {
                      const isSelected = selected.includes(item);
                      return (
                        <StyledTableRow hover key={index} role="checkbox" selected={isSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              className={item.isDisabled ? 'disabledDocument' : 'enabledDocument'}
                              checked={isSelected}
                              onChange={() =>
                                setSelected((prev) =>
                                  isSelected ? prev.filter((i) => i !== item) : [...prev, item]
                                )
                              }
                            />
                          </TableCell>
                          <StyledTableCell>{item.leadId || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>
                            {item.leadDate ? fDate(item.leadDate) : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell>{item.mobile || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>{item.customerName || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>{item.cityName || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>{item.loanName || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>{item.sourceTypeName || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell>
                            <Label
                              variant="ghost"
                              color={item.stageStatusName === 'Not Assigned' ? 'error' : 'success'}
                            >
                              {item.stageStatusName || NOT_AVAILABLE}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell>
                            {item.lastUpdatedOn ? fDate(item.lastUpdatedOn) : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell>{item.assignedToName || NOT_AVAILABLE}</StyledTableCell>
                          <StyledTableCell width={60}>
                            <UserMoreMenu
                              rowId={item.leadId}
                              parent="prospect"
                              companyId={item.companyId}
                              pageMenu={[]}
                              handleDelete={handleDelete}
                              handleOpenDelete={() =>
                                setUi((p) => ({ ...p, openDelete: true, leadId: item.leadId }))
                              }
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {(!ProspectGrid?.prospects || ProspectGrid.prospects.length === 0) && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell align="center" colSpan={TABLE_HEAD.length + 1}>
                          <SearchNotFound searchQuery={filters.name} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            <SimplePagination
              page={ui.page}
              setPage={(val) => setUi((p) => ({ ...p, page: val }))}
              hasNextPage={ui.hasNextPage}
            />
          </Card>
          <Dialog
            open={ui.openDelete}
            onClose={() => setUi((p) => ({ ...p, openDelete: false }))}
            fullWidth
          >
            <Box sx={{ textAlign: 'center' }}>
              <DialogContent>Delete Prospect List</DialogContent>
            </Box>
            <Box m={2}>
              <Typography>Are you sure you want to delete?</Typography>
              <DialogActions>
                <Button
                  onClick={() => setUi((p) => ({ ...p, openDelete: false }))}
                  variant="outlined"
                >
                  Close
                </Button>
                <Button variant="contained" color="secondary" onClick={handleDelete}>
                  Delete
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
          <Loader openLoader={false} />
        </Box>
      </Container>
    </Page>
  );
}
